import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Mail, Sparkles, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * NewsletterSignup, 3 variants:
 *   variant="footer"   → single-row form inside V2Footer
 *   variant="inline"   → boxed card for bottom of blog posts
 *   variant="modal"    → floating popup (see NewsletterModal below)
 * All variants POST to /api/newsletter/subscribe with a source tag.
 */
export default function NewsletterSignup({
  variant = 'inline',
  source = 'inline',
  heading = 'Get one honest email a week.',
  subtext = 'No fluff. No daily nagging. Straight talk on visibility, podcasts and content that actually converts.',
  testId = 'newsletter-signup',
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | ok | error
  const [message, setMessage] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        source,
      });
      setStatus('ok');
      setMessage(res.data?.message || 'You\'re on the list.');
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Something went wrong. Try again?');
    }
  };

  if (variant === 'footer') {
    return (
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2" data-testid={`${testId}-footer`}>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@yourbusiness.com"
          className="flex-1 px-4 py-2.5 bg-white border border-[#E2E8F0] rounded-[10px] text-sm text-[#1E2A44] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#BC516F] transition"
          data-testid={`${testId}-footer-email`}
        />
        <button
          type="submit"
          disabled={status === 'loading' || status === 'ok'}
          className="bg-[#BC516F] hover:bg-[#9A3A54] text-white font-semibold px-5 py-2.5 rounded-[10px] text-sm transition disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid={`${testId}-footer-submit`}
        >
          {status === 'ok' ? <><Check className="w-4 h-4" />Subscribed</> : status === 'loading' ? 'Signing up…' : 'Subscribe'}
        </button>
        {status === 'error' && <div className="text-xs text-red-500 sm:col-span-2">{message}</div>}
      </form>
    );
  }

  // inline (blog bottom) + modal use this richer card layout
  return (
    <div
      className="bg-white border border-[#E2E8F0] rounded-[14px] p-7 sm:p-8 shadow-[0_4px_16px_rgba(30,42,68,0.06)]"
      data-testid={`${testId}-${variant}`}
    >
      <div className="flex items-start gap-4 mb-5">
        <div className="w-11 h-11 shrink-0 rounded-full bg-[#BC516F]/10 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-[#BC516F]" />
        </div>
        <div>
          <h3 className="font-display font-bold text-[#1E2A44] text-xl sm:text-2xl tracking-tight leading-tight">
            {heading}
          </h3>
          <p className="text-[#64748B] mt-2 leading-relaxed text-[15px]">{subtext}</p>
        </div>
      </div>

      {status === 'ok' ? (
        <div
          className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-[10px] p-4 flex items-center gap-3"
          data-testid={`${testId}-success`}
        >
          <div className="w-8 h-8 rounded-full bg-[#22C55E] flex items-center justify-center shrink-0">
            <Check className="w-4 h-4 text-white" />
          </div>
          <p className="text-[#166534] font-medium text-sm">{message}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your first name (optional)"
              className="px-4 py-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[10px] text-sm text-[#1E2A44] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#BC516F] transition"
              data-testid={`${testId}-name`}
            />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.com"
              className="px-4 py-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[10px] text-sm text-[#1E2A44] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#BC516F] transition"
              data-testid={`${testId}-email`}
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full bg-[#BC516F] hover:bg-[#9A3A54] text-white font-semibold px-5 py-3 rounded-[10px] transition disabled:opacity-60 flex items-center justify-center gap-2"
            data-testid={`${testId}-submit`}
          >
            <Mail className="w-4 h-4" />
            {status === 'loading' ? 'Signing up…' : 'Get the emails'}
          </button>
          <p className="text-xs text-[#94A3B8] text-center">
            One honest email a week. Unsubscribe any time.
          </p>
          {status === 'error' && <p className="text-xs text-red-500 text-center">{message}</p>}
        </form>
      )}
    </div>
  );
}

/**
 * NewsletterModal, floating, dismissible popup.
 * Auto-shows after `delayMs` on the page it's mounted on, unless the visitor
 * has previously dismissed or subscribed (stored in localStorage).
 */
export function NewsletterModal({
  delayMs = 20000,
  source = 'modal',
  heading = 'Before you go ,',
  subtext = 'Get one honest email a week from Jo. No fluff, no daily nagging.',
  storageKey = 'nl_modal_dismissed_v1',
  testId = 'newsletter-modal',
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(storageKey) === '1') return;
    } catch (e) {
      /* localStorage blocked, still show */
    }
    const t = setTimeout(() => setOpen(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs, storageKey]);

  const dismiss = () => {
    try { localStorage.setItem(storageKey, '1'); } catch (e) { /* noop */ }
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#1E2A44]/40 backdrop-blur-[2px] px-4 pb-4 sm:pb-0 animate-in fade-in duration-200"
      data-testid={testId}
    >
      <div className="relative w-full max-w-md">
        <button
          onClick={dismiss}
          className="absolute -top-3 -right-3 sm:top-3 sm:right-3 w-9 h-9 rounded-full bg-white border border-[#E2E8F0] shadow flex items-center justify-center text-[#64748B] hover:text-[#1E2A44] transition z-10"
          aria-label="Close"
          data-testid={`${testId}-close`}
        >
          <X className="w-4 h-4" />
        </button>
        <NewsletterSignup
          variant="modal"
          source={source}
          heading={heading}
          subtext={subtext}
          testId={`${testId}-form`}
        />
      </div>
    </div>
  );
}
